'use client';

import { use, useMemo, useRef } from 'react';
import { parseDataset } from './helpers/parseDataset';
import { WebAPIChartSlicerData } from '../../globals/webApiTypes/elements/WebAPICBS';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { sizeContainerCSS } from './cbsGraph.css';
import { position } from '@/uiPrimitives/layout/position';
import { box } from '@/uiPrimitives/layout/box';
import { GraphView } from './GraphView';
import { DataTableView } from './DataTableView';
import { CBSStatlineDataset } from './cbsStatline.types';
import { CbsGraphMeasurementsProvider } from './useCbsGraphMeasurements';
import { useCbsActiveView } from './CbsGraphViewSwitcher.client';

const CbsGraphClient = ({
	chartSlicerData,
	graphDataPromise,
}: {
	chartSlicerData: WebAPIChartSlicerData;
	graphDataPromise: Promise<CBSStatlineDataset>;
}) => {
	const activeView = useCbsActiveView();
	const canvasRef = useRef<HTMLDivElement>(null);

	const dataset = use<CBSStatlineDataset>(graphDataPromise); // use allows data that was being fetched on the server to be used in a client component

	const dataEntryList = useMemo(
		() => parseDataset(dataset, chartSlicerData),
		[dataset, chartSlicerData],
	);

	return (
		<CbsGraphMeasurementsProvider canvasRef={canvasRef}>
			<div
				ref={canvasRef}
				className={classes(
					box({
						aspectRatio: '7 / 5',
					}),
					position({ position: 'relative' }),
					sizeContainerCSS,
				)}
			>
				{activeView === 'graph' && (
					<GraphView
						chartSlicerData={chartSlicerData}
						dataEntryList={dataEntryList}
					/>
				)}
				{activeView === 'table' && (
					<DataTableView
						chartSlicerData={chartSlicerData}
						dataEntryList={dataEntryList}
					/>
				)}
			</div>
		</CbsGraphMeasurementsProvider>
	);
};

export default CbsGraphClient;
